import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { requestValidatePhone } from '../../../actions/phone';
import { EPhoneVerificationStatus } from '../../../constants/phoneVerificationStatus';
import { clearPhoneNumber } from './clearPhoneNumber';
import { TThunkDispatch } from '../../../types/redux';
import { useFormTracking } from '../../../utils/useFormTracking';

export const SERVER_ERROR = 'Произошла ошибка сервера, попробуйте позднее';
export const CODE_ERROR = 'Код указан не верно';

export interface IUseSendVerificationCode {
  verificationCode: string;
  phone: string;

  startCountDown(from: number): void;

  onSendSmsSuccess(): void;

  onVerifySuccess(phone: string): void;
}

export const useSendVerificationCode = ({
  verificationCode,
  onVerifySuccess,
  phone,
  onSendSmsSuccess,
  startCountDown,
}: IUseSendVerificationCode) => {
  const { trackSubmit } = useFormTracking();
  const dispatch = useDispatch<TThunkDispatch>();
  const [error, setError] = useState('');
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    setError('');
  }, [verificationCode, phone]);

  const sendVerificationCode = useCallback(
    async (code: string | null | undefined, phoneNumber: string) => {
      const cleanPhone = clearPhoneNumber(phoneNumber);
      trackSubmit({
        isRequired: true,
        value: null,
        field: 'phone_code',
      });

      setIsFetching(true);
      const smsData = await dispatch(requestValidatePhone({ code, phone: cleanPhone }));
      setIsFetching(false);

      switch (smsData.status) {
        case EPhoneVerificationStatus.Ok:
        case EPhoneVerificationStatus.Skipped:
          setError('');
          onVerifySuccess(cleanPhone);
          break;
        case EPhoneVerificationStatus.Error:
          setError(CODE_ERROR);
          break;
        default:
          setError(SERVER_ERROR);
      }
    },
    [onVerifySuccess, dispatch, trackSubmit],
  );

  const sendVerificationSms = useCallback(
    async (phoneNumber: string) => {
      trackSubmit({
        isRequired: true,
        value: null,
        field: 'phone_number',
      });
      const cleanPhone = clearPhoneNumber(phoneNumber);

      setIsFetching(true);
      const smsData = await dispatch(requestValidatePhone({ phone: cleanPhone }));
      setIsFetching(false);

      switch (smsData.status) {
        case EPhoneVerificationStatus.Ok:
        case EPhoneVerificationStatus.Skipped:
          setError('');
          onVerifySuccess(cleanPhone);
          break;
        case EPhoneVerificationStatus.InProgress:
          setError('');
          startCountDown(Number(smsData.confirmTimeout));
          onSendSmsSuccess();
          break;
        default:
          setError(SERVER_ERROR);
      }
    },
    [onVerifySuccess, onSendSmsSuccess, startCountDown, dispatch, trackSubmit],
  );

  return {
    error,
    isFetching,
    sendVerificationSms,
    sendVerificationCode,
  };
};
